import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import {
  isPushSupported,
  getNotificationPermission,
  requestPermissionAndSubscribe,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '@/services/pushService';

const PushNotificationToggle = ({ token, compact = false }) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const check = async () => {
      const sup = isPushSupported();
      setSupported(sup);
      if (sup) {
        const perm = await getNotificationPermission();
        setPermission(perm);
        const sub = await getCurrentSubscription();
        setSubscribed(!!sub);
      }
    };
    check();
  }, []);

  const handleToggle = async () => {
    if (!supported || !token) return;
    setLoading(true);

    try {
      if (subscribed) {
        await unsubscribeFromPush(token);
        setSubscribed(false);
      } else {
        const sub = await requestPermissionAndSubscribe(token);
        if (sub) {
          setSubscribed(true);
          setPermission('granted');
        } else {
          const perm = await getNotificationPermission();
          setPermission(perm);
        }
      }
    } catch (err) {
      console.error('Push toggle failed:', err);
    }

    setLoading(false);
  };

  if (!supported) return null;

  if (compact) {
    return (
      <button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        className={`p-2 rounded-md transition-colors ${
          subscribed
            ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
        } disabled:opacity-50`}
        title={
          permission === 'denied'
            ? 'Notifications blocked in browser settings'
            : subscribed
            ? 'Disable push notifications'
            : 'Enable push notifications'
        }
        data-testid="push-toggle-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : subscribed ? (
          <Bell className="w-4 h-4" />
        ) : (
          <BellOff className="w-4 h-4" />
        )}
      </button>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200" data-testid="push-notification-toggle">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          subscribed ? 'bg-emerald-500/10' : 'bg-slate-200'
        }`}>
          {subscribed ? (
            <Bell className="w-4 h-4 text-emerald-500" />
          ) : (
            <BellOff className="w-4 h-4 text-slate-400" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800">Push Notifications</p>
          <p className="text-xs text-slate-500">
            {permission === 'denied'
              ? 'Blocked in browser settings'
              : subscribed
              ? 'Receiving payment reminders & updates'
              : 'Get notified about payments & tickets'}
          </p>
        </div>
      </div>
      <button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          subscribed
            ? 'bg-slate-200 text-slate-600 hover:bg-slate-300'
            : 'bg-emerald-500 text-white hover:bg-emerald-600'
        } disabled:opacity-50`}
        data-testid="push-toggle-action"
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : subscribed ? (
          'Disable'
        ) : (
          'Enable'
        )}
      </button>
    </div>
  );
};

export default PushNotificationToggle;
